/**
 * <bundle-offers> — Bundle offers (Version 2), see snippets/bundles-widget-v2.liquid
 *
 * Card: shows bundle total / combined price / saving, kept in sync with the page's selected variant.
 * Popup (<dialog>): steps
 *   1. main    – this product's options (skipped if it has no options)
 *   2. accessory – the paired product's options (skipped if none)
 *   3. addon   – optional full-price add-on, e.g. coffee (skipped if none)
 * The price breakdown below the steps updates live. "Add bundle to cart" adds everything in one
 * /cart/add request. The bundle saving is applied by a Shopify automatic discount (Buy X get Y).
 */
if (!customElements.get('bundle-offers')) {
  class BundleOffers extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === 'true') return;
      this.dataset.initialised = 'true';

      this.mainVariants = this.readJSON(this.querySelector('script[data-main-variants]')) || [];
      const selectedId = Number(this.readJSON(this.querySelector('script[data-main-selected]')));
      this.mainVariant = this.mainVariants.find((v) => v.id === selectedId) || this.mainVariants[0];

      try {
        this.formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'AUD',
        });
      } catch (e) {
        this.formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'AUD' });
      }

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.querySelectorAll('dialog[data-bundle-dialog]').forEach((dialog) => {
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close(); // backdrop click
        });
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event?.data?.variant;
          if (!variant || !variant.id) return;
          this.setMainVariant(variant.id);
        });
      }

      this.updateCards();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    /* ---------- helpers ---------- */

    readJSON(el) {
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.error('Bundle offers: invalid JSON', e);
        return null;
      }
    }

    money(cents) {
      return this.formatter.format(Number(cents || 0) / 100);
    }

    /** RRP = compare-at price when it's higher than the price, otherwise the price. */
    rrp(variant) {
      if (!variant) return 0;
      return Math.max(Number(variant.compare || 0), Number(variant.price || 0));
    }

    variantLabel(variant) {
      return variant && variant.title && variant.title !== 'Default Title' ? `(${variant.title})` : '';
    }

    /* ---------- cards ---------- */

    syncMainVariantFromForm() {
      const scope = this.closest('section[id^="MainProduct-"]') || document;
      const input = scope.querySelector('product-form form[data-type="add-to-cart-form"] input[name="id"]');
      if (input && input.value) this.setMainVariant(input.value, false);
    }

    setMainVariant(id, refresh = true) {
      const match = this.mainVariants.find((v) => v.id === Number(id));
      if (!match) return;
      this.mainVariant = match;
      if (refresh) this.updateCards();
    }

    updateCards() {
      if (!this.mainVariant) return;
      this.querySelectorAll('[data-bundle-offer]').forEach((card) => {
        const save = Number(card.dataset.save || 0);
        const accessory = { price: Number(card.dataset.accessoryPrice || 0), compare: Number(card.dataset.accessoryCompare || 0) };
        const bundleTotal = Math.max(this.mainVariant.price + accessory.price - save, 0);
        const combinedRrp = this.rrp(this.mainVariant) + this.rrp(accessory);
        const saving = Math.max(combinedRrp - bundleTotal, 0);
        const info = card.querySelector(':scope > .bundle-offer__info');
        if (!info) return;
        const total = info.querySelector('[data-bundle-total]');
        const was = info.querySelector('[data-bundle-combined]');
        const saveEl = info.querySelector('[data-bundle-saving]');
        if (total) total.textContent = this.money(bundleTotal);
        if (was) was.textContent = this.money(combinedRrp);
        if (saveEl) saveEl.textContent = this.money(saving).replace(/\.00$/, '');
        info.querySelectorAll('.bundle-offer__price-was, .bundle-offer__save').forEach((el) => (el.hidden = saving === 0));
      });
    }

    /* ---------- events ---------- */

    onClick(event) {
      const opener = event.target.closest('[data-bundle-open]');
      if (opener) {
        const dialog = this.querySelector(`#${CSS.escape(opener.dataset.bundleOpen)}`);
        if (dialog) this.openDialog(dialog, opener);
        return;
      }

      const dialog = event.target.closest('dialog[data-bundle-dialog]');
      if (!dialog) return;

      if (event.target.closest('[data-bundle-close]')) return dialog.close();
      if (event.target.closest('[data-step-next]')) return this.goToStep(dialog, dialog._stepIndex + 1);
      if (event.target.closest('[data-step-back]')) return this.goToStep(dialog, dialog._stepIndex - 1);

      const go = event.target.closest('[data-step-go]');
      if (go) {
        const index = dialog._steps.indexOf(go.dataset.stepGo);
        if (index !== -1 && index <= dialog._maxStep) this.goToStep(dialog, index);
        return;
      }

      const add = event.target.closest('[data-bundle-add]');
      if (add) this.addBundle(dialog, add);
    }

    onChange(event) {
      const dialog = event.target.closest('dialog[data-bundle-dialog]');
      if (!dialog || !event.target.matches('input[type="radio"]')) return;

      const role = event.target.dataset.role;
      if (role === 'main' || role === 'accessory') {
        const variant = this.resolveVariant(dialog, role);
        dialog[role === 'main' ? '_main' : '_accessory'] = variant;
        this.refreshPicker(dialog, role, variant);
        this.showRoleImage(dialog, role, variant);
      }
      this.updateDialog(dialog);
    }

    /* ---------- dialog ---------- */

    getAccessoryVariants(dialog) {
      if (!dialog._variants) {
        dialog._variants = this.readJSON(dialog.querySelector('script[data-accessory-variants]')) || [];
      }
      return dialog._variants;
    }

    variantsFor(dialog, role) {
      return role === 'main' ? this.mainVariants : this.getAccessoryVariants(dialog);
    }

    selectedOptions(dialog, role) {
      const selected = [];
      dialog.querySelectorAll(`input[data-role="${role}"]:checked`).forEach((input) => {
        selected[Number(input.dataset.optionIndex)] = input.value;
      });
      return selected;
    }

    resolveVariant(dialog, role) {
      const options = this.selectedOptions(dialog, role);
      const variants = this.variantsFor(dialog, role);
      if (!options.length) return variants.find((v) => v.available) || variants[0];
      return variants.find((v) => (v.options || []).every((value, i) => options[i] === undefined || options[i] === value)) || null;
    }

    /** Ticks the radios to match a variant (used when opening the popup). */
    applyVariantToPicker(dialog, role, variant) {
      if (!variant || !variant.options) return;
      variant.options.forEach((value, i) => {
        dialog.querySelectorAll(`input[data-role="${role}"][data-option-index="${i}"]`).forEach((input) => {
          input.checked = input.value === value;
        });
      });
    }

    /** Updates "Colour: Black" labels and greys out values with no available variant. */
    refreshPicker(dialog, role, variant) {
      const options = this.selectedOptions(dialog, role);
      const variants = this.variantsFor(dialog, role);

      dialog.querySelectorAll(`.bundle-step[data-step="${role}"] .bundle-picker`).forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        const label = fieldset.querySelector('[data-option-selected]');
        if (label) label.textContent = options[index] || '';

        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const available = variants.some(
            (v) =>
              v.available &&
              (v.options || []).every((value, i) => (i === index ? value === input.value : options[i] === undefined || options[i] === value))
          );
          input.closest('.bundle-picker__value').classList.toggle('is-unavailable', !available);
        });
      });

      const step = dialog.querySelector(`.bundle-step[data-step="${role}"]`);
      if (step) step.classList.toggle('has-error', !variant || !variant.available);
    }

    showRoleImage(dialog, role, variant) {
      const image = dialog.querySelector('[data-bundle-image]');
      if (!image || image.hasAttribute('data-bundle-image-fixed')) return;
      if (variant && variant.image) image.src = variant.image;
    }

    getSelectedAddon(dialog) {
      const input = dialog.querySelector('[data-bundle-addon] input[type="radio"]:checked');
      if (!input || !input.value) return null;
      return { id: Number(input.value), title: input.dataset.addonTitle, price: Number(input.dataset.addonPrice || 0) };
    }

    openDialog(dialog, opener) {
      this.syncMainVariantFromForm();
      this.updateCards();
      this.hideError(dialog);

      // Steps present in this popup, in order
      dialog._steps = Array.from(dialog.querySelectorAll('.bundle-step')).map((el) => el.dataset.step);
      dialog._maxStep = 0;

      // Start from the variant selected on the page and the accessory's first available variant
      const accessoryVariants = this.getAccessoryVariants(dialog);
      dialog._main = this.mainVariant;
      dialog._accessory = dialog._accessory || accessoryVariants.find((v) => v.available) || accessoryVariants[0];
      this.applyVariantToPicker(dialog, 'main', dialog._main);
      this.applyVariantToPicker(dialog, 'accessory', dialog._accessory);
      this.refreshPicker(dialog, 'main', dialog._main);
      this.refreshPicker(dialog, 'accessory', dialog._accessory);

      this.goToStep(dialog, 0);

      dialog._opener = opener;
      dialog.addEventListener('close', () => dialog._opener?.focus(), { once: true });
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    }

    stepIsValid(dialog, step) {
      if (step === 'main') return !!(dialog._main && dialog._main.available);
      if (step === 'accessory') return !!(dialog._accessory && dialog._accessory.available);
      return true;
    }

    goToStep(dialog, index) {
      const steps = dialog._steps || [];
      const max = Math.max(steps.length - 1, 0);
      const target = Math.min(Math.max(index, 0), max);

      // Can't move forward past an invalid step
      for (let i = 0; i < target; i++) {
        if (!this.stepIsValid(dialog, steps[i])) return this.goToStep(dialog, i);
      }

      dialog._stepIndex = target;
      dialog._maxStep = Math.max(dialog._maxStep || 0, target);
      const current = steps[target];

      dialog.querySelectorAll('.bundle-step').forEach((el) => {
        el.hidden = el.dataset.step !== current;
      });

      dialog.querySelectorAll('[data-step-go]').forEach((button) => {
        const i = steps.indexOf(button.dataset.stepGo);
        const li = button.closest('li');
        li.classList.toggle('is-current', i === target);
        li.classList.toggle('is-done', i < target);
        button.disabled = i > dialog._maxStep;
        button.querySelector('.bundle-steps__num').textContent = i < target ? '✓' : String(i + 1);
        if (i === target) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });

      // Image follows the step's product (unless a fixed bundle image is set)
      if (current === 'main') this.showRoleImage(dialog, 'main', dialog._main);
      if (current === 'accessory') this.showRoleImage(dialog, 'accessory', dialog._accessory);

      this.updateDialog(dialog);

      const heading = dialog.querySelector(`.bundle-step[data-step="${current}"] .bundle-step__heading`);
      if (heading && dialog.open) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: false });
      }
    }

    updateDialog(dialog) {
      const main = dialog._main;
      const accessory = dialog._accessory;
      const addon = this.getSelectedAddon(dialog);
      const save = Number(dialog.dataset.save || 0);
      const set = (selector, value) => {
        const el = dialog.querySelector(selector);
        if (el) el.textContent = value;
      };

      set('[data-main-variant-title]', this.variantLabel(main));
      set('[data-accessory-variant-title]', this.variantLabel(accessory));
      // Lines show RRP; "Bundle saving" = RRP total − bundle price, so the breakdown adds up.
      set('[data-main-price]', main ? this.money(this.rrp(main)) : '—');
      set('[data-accessory-price]', accessory ? this.money(this.rrp(accessory)) : '—');

      const subtotal = main && accessory ? Math.max(main.price + accessory.price - save, 0) : 0;
      if (main && accessory) {
        set('[data-bundle-saving]', this.money(Math.max(this.rrp(main) + this.rrp(accessory) - subtotal, 0)));
      }
      set('[data-bundle-total]', this.money(subtotal + (addon ? addon.price : 0)));

      const addonLine = dialog.querySelector('[data-addon-line]');
      if (addonLine) {
        addonLine.hidden = !addon;
        set('[data-addon-line-title]', addon ? addon.title : '');
        set('[data-addon-line-price]', addon ? this.money(addon.price) : '');
      }

      // Footer buttons
      const steps = dialog._steps || [];
      const index = dialog._stepIndex || 0;
      const isLast = index >= steps.length - 1;
      const currentValid = this.stepIsValid(dialog, steps[index]);
      const allValid = !!(main && main.available && accessory && accessory.available);

      const back = dialog.querySelector('[data-step-back]');
      const next = dialog.querySelector('[data-step-next]');
      const add = dialog.querySelector('[data-bundle-add]');
      if (back) back.hidden = index === 0;
      if (next) {
        next.hidden = isLast;
        next.disabled = !currentValid;
      }
      if (add) {
        add.hidden = !isLast;
        add.toggleAttribute('disabled', !allValid);
      }

      this.hideError(dialog);
      if (!currentValid) {
        const variant = steps[index] === 'main' ? main : accessory;
        this.showError(
          dialog,
          variant ? 'That option is sold out. Please choose another.' : "That combination isn't available. Please choose another."
        );
      }
    }

    showError(dialog, message) {
      const el = dialog.querySelector('[data-bundle-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = false;
    }

    hideError(dialog) {
      const el = dialog.querySelector('[data-bundle-error]');
      if (el) el.hidden = true;
    }

    setLoading(button, isLoading) {
      const spinner = button.querySelector('.loading__spinner');
      button.classList.toggle('loading', isLoading);
      button.toggleAttribute('aria-disabled', isLoading);
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    }

    /* ---------- cart ---------- */

    addBundle(dialog, button) {
      if (button.getAttribute('aria-disabled') === 'true') return;
      const main = dialog._main;
      const accessory = dialog._accessory;
      if (!main || !accessory || !main.available || !accessory.available) return;
      this.hideError(dialog);

      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const sections = [];
      [cartDrawer, cartNotification].forEach((target) => {
        if (target && typeof target.getSectionsToRender === 'function') {
          sections.push(...target.getSectionsToRender().map((s) => s.section || s.id).filter(Boolean));
        }
      });

      const properties = { _added_in: 'bundle-offer' };
      const payload = {
        items: [
          { id: main.id, quantity: 1, properties },
          { id: accessory.id, quantity: 1, properties },
        ],
        sections_url: window.location.pathname,
      };
      const addon = this.getSelectedAddon(dialog);
      if (addon) payload.items.push({ id: addon.id, quantity: 1, properties: { _added_in: 'bundle-offer-addon' } });
      if (sections.length) payload.sections = [...new Set(sections)];

      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig('json')
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      config.headers = { ...(config.headers || {}), 'X-Requested-With': 'XMLHttpRequest' };
      config.body = JSON.stringify(payload);

      this.setLoading(button, true);

      fetch((window.routes && window.routes.cart_add_url) || this.dataset.cartAddUrl, config)
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) {
            const message = data.description || data.message || 'Unable to add this bundle. Please try again.';
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, { source: 'bundle-offer', errors: data.errors || message });
            }
            this.showError(dialog, message);
            return;
          }

          if (!cartDrawer && !cartNotification) {
            window.location = (window.routes && window.routes.cart_url) || this.dataset.cartUrl;
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-offer', cartData: data });
          }

          dialog.close();

          if (data.sections && cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data);
            cartDrawer.classList.remove('is-empty');
          }

          document.querySelectorAll('.header__icon--cart').forEach((icon) => icon.classList.remove('cart--empty'));

          if (typeof showMessage === 'function') showMessage('Bundle added to cart.', 'success');

          this.syncPickupLocation();
        })
        .catch((error) => {
          console.error('Bundle offers: add to cart failed', error);
          this.showError(dialog, 'Unable to add this bundle. Please try again.');
        })
        .finally(() => this.setLoading(button, false));
    }

    /** Mirrors product-form.js: keep the chosen click & collect location on the cart. */
    syncPickupLocation() {
      let locationId = null;
      try {
        locationId = localStorage.getItem('pickup_location_id');
      } catch (e) {
        return;
      }
      if (!locationId || !window.Shopify?.routes) return;
      const formData = new FormData();
      formData.append('attributes[PickupLocationID]', locationId);
      fetch(window.Shopify.routes.root + 'cart/update.js', { method: 'POST', body: formData }).catch(() => {});
    }
  }

  customElements.define('bundle-offers', BundleOffers);
}
